import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Warning, CaretDown, CaretRight, MagnifyingGlass, Package as PackageIcon, Trash } from "@phosphor-icons/react";
import { fmtDateTime as fmtDate } from "@/lib/dates";

export default function FaultyPartsPage() {
  const { user } = useAuth();
  const canScrap = ["supervisor", "manager"].includes(user?.role);
  const [data, setData] = useState(null);
  const [stockById, setStockById] = useState({});   // stock_code -> {id, faulty_qty}
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(new Set());
  const [scrapping, setScrapping] = useState(null); // {stockId, name, faultyQty}

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: fp }, { data: stock }] = await Promise.all([
        api.get("/records/faulty-parts"),
        canScrap ? api.get("/stock", { params: { limit: 10000 } }) : Promise.resolve({ data: [] }),
      ]);
      setData(fp);
      const map = {};
      (stock || []).forEach((s) => {
        if (s.item) map[s.item] = { id: s.id, faulty_qty: Number(s.faulty_qty || 0) };
      });
      setStockById(map);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    if (!data?.parts) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.parts;
    return data.parts.filter((p) => {
      if ((p.name || "").toLowerCase().includes(q)) return true;
      if ((p.stock_code || "").toLowerCase().includes(q)) return true;
      if ((p.group || "").toLowerCase().includes(q)) return true;
      return p.entries.some((e) =>
        (e.engineer_name || "").toLowerCase().includes(q)
        || (e.receipt_no || "").toLowerCase().includes(q)
        || (e.fault_notes || "").toLowerCase().includes(q)
      );
    });
  }, [data, search]);

  const toggle = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  return (
    <div className="min-h-screen">
      <div className="border-b border-neutral-200 bg-white/80 backdrop-blur-xl sticky top-0 z-10 no-print">
        <div className="px-4 py-4 md:px-10 md:py-6 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="label-eyebrow">Defective parts registry</div>
            <h1 className="font-display text-3xl md:text-4xl tracking-tight leading-none mt-2">Faulty Parts</h1>
            <p className="text-sm text-neutral-500 mt-2 max-w-xl hidden md:block">
              Every faulty part brought back from site. These never re-enter physical stock —
              this ledger tracks what's sitting in the store as defective and who returned it.
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="label-eyebrow">Unique parts</div>
              <div className="font-display text-3xl leading-none" data-testid="faulty-unique-count">{data?.total_parts ?? "—"}</div>
            </div>
            <div className="text-right">
              <div className="label-eyebrow">Total qty</div>
              <div className="font-display text-3xl leading-none" data-testid="faulty-total-qty">{data?.total_qty ?? "—"}</div>
            </div>
            <div className="text-right border-l border-neutral-200 pl-4">
              <div className="label-eyebrow">Faulty stock on hand</div>
              <div className="font-display text-3xl leading-none text-red-700" data-testid="faulty-stock-on-hand">
                {data?.faulty_stock_on_hand ?? "—"}
              </div>
              <div className="text-[10px] font-mono text-neutral-500">
                across {data?.faulty_stock_skus ?? 0} SKUs
              </div>
            </div>
          </div>
        </div>
        <div className="px-4 pb-4 md:px-10">
          <div className="relative max-w-md">
            <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by part, technician, IV # or fault reason…"
              className="h-10 pl-9 rounded-none border-neutral-400 font-mono text-sm"
              data-testid="faulty-search"
            />
          </div>
        </div>
      </div>

      <div className="p-4 md:p-10">
        <div className="border border-neutral-200 bg-white">
          <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-neutral-50 border-b border-neutral-200 text-[10px] font-mono uppercase tracking-wider text-neutral-600">
            <div className="col-span-1"></div>
            <div className="col-span-4">Part</div>
            <div className="col-span-2">Group</div>
            <div className="col-span-2 text-right">Total qty</div>
            <div className="col-span-2 text-right text-red-700">Faulty bucket</div>
            <div className="col-span-1 text-right">Actions</div>
          </div>
          {loading && <div className="p-8 text-center text-xs text-neutral-400">Loading…</div>}
          {!loading && filtered.length === 0 && (
            <div className="p-10 text-center text-sm text-neutral-500 flex flex-col items-center gap-2">
              <Warning size={20} weight="duotone" />
              {search ? `No matches for "${search}"` : "No faulty parts on record."}
            </div>
          )}
          {!loading && filtered.map((p) => {
            const key = p.stock_code || p.name;
            const open = expanded.has(key);
            const stock = p.stock_code ? stockById[p.stock_code] : null;
            const bucketQty = stock?.faulty_qty || 0;
            return (
              <div key={key} className="border-b border-neutral-100 last:border-0" data-testid={`faulty-row-${key}`}>
                <div className="grid grid-cols-12 gap-2 px-3 py-3 items-center hover:bg-neutral-50">
                  <button
                    onClick={() => toggle(key)}
                    className="col-span-1 flex items-center justify-start"
                    data-testid={`faulty-toggle-${key}`}
                  >
                    {open ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
                  </button>
                  <button onClick={() => toggle(key)} className="col-span-4 min-w-0 text-left">
                    <div className="flex items-center gap-1.5">
                      <PackageIcon size={14} weight="duotone" className="shrink-0" />
                      <span className="font-medium truncate">{p.name}</span>
                    </div>
                    {p.stock_code && <div className="text-[10px] font-mono text-neutral-500 truncate mt-0.5">{p.stock_code}</div>}
                  </button>
                  <div className="col-span-2 text-xs font-mono text-neutral-600">{p.group || "—"}</div>
                  <div className="col-span-2 text-right font-mono">{p.total_qty}</div>
                  <div
                    className={`col-span-2 text-right font-mono text-lg ${bucketQty > 0 ? "text-red-700 font-semibold" : "text-neutral-400"}`}
                    data-testid={`faulty-bucket-${key}`}
                    title="Live faulty_qty on the stock master"
                  >
                    {bucketQty}
                  </div>
                  <div className="col-span-1 flex justify-end">
                    {canScrap && stock && bucketQty > 0 ? (
                      <button
                        onClick={() => setScrapping({ stockId: stock.id, name: p.name, faultyQty: bucketQty })}
                        data-testid={`scrap-btn-${key}`}
                        title="Write off (scrap) faulty stock"
                        className="px-2 py-1 border border-red-600 text-red-700 hover:bg-red-600 hover:text-white flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider"
                      >
                        <Trash size={11} weight="bold" /> Scrap
                      </button>
                    ) : null}
                  </div>
                </div>
                {open && (
                  <div className="px-3 pb-3 bg-neutral-50/40" data-testid={`faulty-drill-${key}`}>
                    <div className="grid grid-cols-12 gap-2 px-3 py-2 text-[10px] font-mono uppercase tracking-wider text-neutral-500 border-b border-neutral-200">
                      <div className="col-span-2">Date</div>
                      <div className="col-span-3">Technician</div>
                      <div className="col-span-2">Region</div>
                      <div className="col-span-1 text-right">Qty</div>
                      <div className="col-span-2">Vouchers</div>
                      <div className="col-span-2">Fault reason</div>
                    </div>
                    {p.entries.map((e, i) => (
                      <div key={i} className="grid grid-cols-12 gap-2 px-3 py-2 items-start text-xs border-b border-neutral-100 last:border-0">
                        <div className="col-span-2 font-mono text-[11px]">{fmtDate(e.returned_at)}</div>
                        <div className="col-span-3 font-medium truncate flex items-center gap-1.5" title={e.engineer_name}>
                          <span className={`px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider shrink-0 ${
                            e.source === "used" ? "bg-neutral-200 text-neutral-800" : "bg-red-100 text-red-800"
                          }`}>
                            {e.source || "faulty"}
                          </span>
                          <span className="truncate">{e.engineer_name || "—"}</span>
                        </div>
                        <div className="col-span-2 font-mono text-[11px]">{e.region || "—"}</div>
                        <div className="col-span-1 text-right font-mono">{e.quantity}</div>
                        <div className="col-span-2 font-mono text-[11px] space-y-0.5">
                          <div>{e.receipt_no || "—"}</div>
                          {e.return_receipt_no && <div className="text-neutral-500">{e.return_receipt_no}</div>}
                        </div>
                        <div className="col-span-2 italic text-neutral-700">
                          {e.fault_notes || <span className="text-neutral-400 not-italic">(no note)</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {scrapping && (
        <ScrapDialog
          target={scrapping}
          onClose={() => setScrapping(null)}
          onSaved={() => { setScrapping(null); load(); }}
        />
      )}
    </div>
  );
}


function ScrapDialog({ target, onClose, onSaved }) {
  const [qty, setQty] = useState(target.faultyQty);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const q = Number(qty) || 0;
    if (q <= 0) return toast.error("Qty must be > 0");
    if (q > target.faultyQty) return toast.error(`Only ${target.faultyQty} in the faulty bucket`);
    setBusy(true);
    try {
      const { data } = await api.post(`/stock/${target.stockId}/scrap`, { qty: q, reason });
      toast.success(`Scrapped ${data.scrapped} × ${target.name} — bucket ${data.before} → ${data.after}`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md rounded-none border-neutral-900" data-testid="scrap-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl tracking-tight">Scrap Faulty Stock</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-sm">
            <span className="font-medium">{target.name}</span>
            <div className="text-[11px] text-neutral-500 font-mono mt-0.5">
              Currently in bucket: <b>{target.faultyQty}</b>
            </div>
          </div>
          <div className="space-y-1">
            <div className="label-eyebrow">Qty to write off</div>
            <Input
              type="number"
              min={1}
              max={target.faultyQty}
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              className="rounded-none border-neutral-400 h-10 font-mono"
              data-testid="scrap-qty"
            />
          </div>
          <div className="space-y-1">
            <div className="label-eyebrow">Reason (optional)</div>
            <Textarea
              rows={2}
              placeholder="e.g. sent to disposal, warranty-claim rejected, cannibalised for spares…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="rounded-none border-neutral-400"
              data-testid="scrap-reason"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-white">Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy}
            data-testid="scrap-submit"
            className="rounded-none bg-red-600 text-white hover:bg-red-700"
          >
            {busy ? "Scrapping…" : "Confirm Scrap"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
